import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { KioskProvider, useKioskContext } from '../context/KioskContext';

const KioskLandingPageWrapper = () => (
  <KioskProvider>
    <KioskLandingPageContent />
  </KioskProvider>
);

const fallbackSlides = [
  'https://images.unsplash.com/photo-1482049016688-2d3e1b311543?fm=jpg&q=60&w=3000',
  'https://plus.unsplash.com/premium_photo-1673108852141-e8c3c22a4a22?q=80&w=2940&auto=format&fit=crop',
  'https://images.unsplash.com/photo-1512621776951-a57141f2eefd?q=80&w=2940&auto=format&fit=crop',
];

const isMongoId = (s) => /^[a-f0-9]{24}$/i.test(s || '');

const slugToId = {
  test: '672312ec5b53fbbe37d4b0d5',
  khiang: '67d3a2144a266ea37ce7ac69',
  khiangmpob: '680e35b78a5a51ec452b6678',
  ebcbaguio: '689154b4dd8b04c79d293a3d',
};

const isVideoUrl = (u) => {
  try {
    const ext = (new URL(u).pathname.split('.').pop() || '').toLowerCase();
    return ['mp4', 'webm', 'ogg', 'ogv', 'mov', 'm4v'].includes(ext);
  } catch {
    const ext = (u.split('?')[0].split('.').pop() || '').toLowerCase();
    return ['mp4', 'webm', 'ogg', 'ogv', 'mov', 'm4v'].includes(ext);
  }
};

const pickUrl = (o) =>
  o?.url ||
  o?.mediaUrl ||
  o?.imageUrl ||
  o?.photoUrl ||
  o?.s3Url ||
  o?.path ||
  o?.image?.url ||
  o?.image?.mediaUrl ||
  o?.image?.path ||
  null;

  const extractHomeMedia = (payload) => {
    const media =
      (Array.isArray(payload?.media) && payload.media) ||
      (Array.isArray(payload?.data?.media) && payload.data.media) ||
      (Array.isArray(payload?.result?.media) && payload.result.media) ||
      [];
  
    return media
      .filter((m) => (m?.isActive ?? true))
      .sort((a, b) => (a?.order ?? 0) - (b?.order ?? 0))
      .map((m) => m?.url)
      .filter(Boolean);
  };

const KioskLandingPageContent = () => {
  const navigate = useNavigate();
  const { posId: rawPos } = useParams();
  const { translate } = useKioskContext();

  const resolvedPosId = isMongoId(rawPos) ? rawPos : slugToId[(rawPos || '').toLowerCase()] || rawPos;

  const [slides, setSlides] = useState(fallbackSlides);
  const [currentIndex, setCurrentIndex] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => {
    let mounted = true;
    const fetchHome = async () => {
      try {
        if (!resolvedPosId) return;
        const { data } = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/v1/api/promotion/public/home`,
          { params: { posId: resolvedPosId } }
        );
        const urls = extractHomeMedia(data);
        if (mounted) {
          setSlides(Array.isArray(urls) && urls.length ? urls : fallbackSlides);
          setCurrentIndex(0);
        }
      } catch {
        if (mounted) {
          setSlides(fallbackSlides);
          setCurrentIndex(0);
        }
      }
    };
    fetchHome();
    return () => {
      mounted = false;
    };
  }, [resolvedPosId]);

  useEffect(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    const current = slides[currentIndex];
    if (!current) return;
    if (!isVideoUrl(current)) {
      timerRef.current = setInterval(() => {
        setCurrentIndex((p) => (p + 1) % slides.length);
      }, 5000);
    }
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [slides, currentIndex]);

  const handleVideoEnded = () => {
    setCurrentIndex((prev) => (prev + 1) % slides.length);
  };

  const handleStartOrder = () => {
    navigate(`/${rawPos}/order`);
  };

  return (
    <Box
      sx={{
        height: '100vh',
        width: '100vw',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        position: 'relative',
        cursor: 'pointer',
      }}
      onClick={handleStartOrder}
    >
      <Box sx={{ height: '90vh', width: '100%', position: 'relative', overflow: 'hidden' }}>
        {slides.map((src, idx) => {
          const active = idx === currentIndex;
          const z = active ? 2 : 1;

          if (isVideoUrl(src)) {
            return (
              <Box
                key={`${src}-${idx}`}
                sx={{
                  position: 'absolute',
                  inset: 0,
                  opacity: active ? 1 : 0,
                  transition: 'opacity 0.5s ease-in-out',
                  zIndex: z,
                  backgroundColor: 'black',
                }}
              >
                {active && (
                  <video
                    key={src}
                    src={src}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    autoPlay
                    muted
                    playsInline
                    controls={false}
                    onEnded={handleVideoEnded}
                    onError={handleVideoEnded}
                  />
                )}
              </Box>
            );
          }

          return (
            <Box
              key={`${src}-${idx}`}
              sx={{
                position: 'absolute',
                inset: 0,
                opacity: active ? 1 : 0,
                transition: 'opacity 1.5s ease-in-out',
                backgroundImage: `url(${src})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                zIndex: z,
              }}
            />
          );
        })}

        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.4)',
            zIndex: 3,
          }}
        />

        <Box
          sx={{
            position: 'absolute',
            top: '40%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            textAlign: 'center',
            zIndex: 4,
            px: 2,
          }}
        >
          <Typography
            variant="h1"
            component="h1"
            sx={{
              color: 'white',
              fontWeight: 'bold',
              textShadow: '2px 2px 4px rgba(0,0,0,0.5)',
              mb: 2,
              fontSize: { xs: '2.25rem', sm: '3rem', md: '4rem' },
            }}
          >
            {translate('Welcome', { en: 'Welcome', zh: '欢迎', th: 'ยินดีต้อนรับ', tl: 'Maligayang pagdating' })}
          </Typography>
          <Typography
            variant="h4"
            sx={{
              color: 'white',
              textShadow: '1px 1px 2px rgba(0,0,0,0.5)',
              fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2rem' },
            }}
          >
            {translate('Touch anywhere to start', {
              en: 'Touch anywhere to start',
              zh: '触摸任意位置开始',
              th: 'แตะที่ใดก็ได้เพื่อเริ่ม',
              tl: 'Pindutin kahit saan para magsimula',
            })}
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          height: '10vh',
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: 'primary.main',
          zIndex: 5,
        }}
      >
        <Button
          variant="contained"
          color="primary"
          sx={{
            width: '100%',
            height: '100%',
            borderRadius: 0,
            fontSize: '1.5rem',
            fontWeight: 'bold',
            '&:hover': { backgroundColor: 'primary.dark' },
          }}
          onClick={handleStartOrder}
        >
          {translate('Start Order', {
            en: 'Start Order',
            zh: '开始订购',
            th: 'เริ่มการสั่งซื้อ',
            tl: 'Simulan ang Order',
          })}
        </Button>
      </Box>
    </Box>
  );
};

export default KioskLandingPageWrapper;
